<template>
	<div class="notifications list-group list-group-flush list-group-activity mb-2">
		<div
			v-if="!scopedNotifications.data.length"
			class="list-group-item text-center">
			No notifications to display
		</div>
		<div
			v-for="notification in scopedNotifications.data"
			:key="notification.id"
			class="notification list-group-item">
			<a
				href="#"
				class="stretched-link p-0 text-reset"
				@click="onClick($event, notification)">
				<div class="row">
					<div class="col-auto">
						<div class="avatar avatar-sm">
							<div class="avatar-title font-size-lg rounded-circle text-primary">
								<i
									class="fas"
									:class="getIconClass(notification)"></i>
							</div>
						</div>
					</div>
					<div class="col ml-n2">
						<div class="d-flex justify-content-between align-items-center">
							<h5 class="mb-1">
								{{ notification.title }}
							</h5>
							<button
								v-if="belongsToUser(notification) && !notification.read_at"
								class="btn btn-sm btn-outline-danger float-right py-0"
								style="font-size: 0.8em; z-index: 9999; position: relative;"
								@click.stop="archiveNotification(notification.id)">
								Archive
							</button>
							<BBadge v-if="notification.read_at" variant="light">Archived</BBadge>
						</div>
						<p v-if="!notification.is_announcement" class="small text-gray-700 mb-0"
							v-html="notification.body" />
						<small class="text-muted">
							{{ moment(notification.created_at).format('llll') }}
						</small>
					</div>
				</div>
			</a>
		</div>
		<template v-if="showPagination">
			<BPagination
				v-show="scopedNotifications.meta.last_page > 1"
				class="mt-2"
				:model-value="scopedNotifications.meta.current_page"
				:total-rows="scopedNotifications.meta.total"
				:per-page="scopedNotifications.meta.per_page"
				@update:model-value="paginate"></BPagination>
		</template>
	</div>
</template>

<script>
	import { useValidation } from '@js/composables/useValidation.js';
	import {mapGetters} from 'vuex';

	export default {
		props: {
			showPagination: {
				type: Boolean,
				required: false,
				default: () => false,
			},
			/**
			 * If no userId is provided, the authenticated user's notifications will be fetched.
			 */
			userId: {
				type: Number,
				required: false,
				default: () => null
			}
		},
		setup() {
			return {
				...useValidation(),
			};
		},
		data() {
			return {
				notifications: {
					data: [],
					meta: {}
				}
			}
		},
		computed: {
			...mapGetters({
				user: 'auth/user'
			}),
			/**
			 * If a userId is provided, return the locally stored notifications, else return the notifications from state
			 */
			scopedNotifications() {
				if (this.userId) {
					return this.notifications;
				}

				return this.$store.getters['notifications/getNotifications'];
			}
		},
		watch: {
			userId() {
				this.fetchNotifications();
			}
		},
		created() {
			this.fetchNotifications()

			this.$eventBus.$on('notifications.refresh', () => this.fetchNotifications(1))
		},
		beforeUnmount() {
			this.$eventBus.$off('notifications.refresh')
		},
		methods: {
			fetchNotifications(page) {
				if(!this.userId) {
					return;
				}

				this.$axios.get('/api/notifications', {
					params: {
						page,
						archived: false,
						user_id: this.userId
					}
				}).then(response => {
					this.notifications = response.data;
				}).catch(error => {
					this.handleError(error);
					toastr.error('An error occurred while loading notifications.');
				})
			},
			async archiveNotification(notification_id) {
				await this.$store.dispatch('notifications/archiveNotification', notification_id);

				this.$eventBus.$emit('notifications.refresh');
			},
			paginate(page) {
				if (this.userId) {
					return this.fetchNotifications(page);
				}

				this.$store.dispatch('notifications/fetchNotifications', {
					page: page,
				});
			},
			getIconClass(notification) {
				switch (notification.type) {
					case 'success':
						return 'fas fa-check text-success';
					case 'info':
						return 'fas fa-dot-circle text-info';
					case 'warning':
						return 'fas fa-exclamation-circle text-warning';
					default:
						return 'fas fa-dot-circle text-info';
				}
			},
			onClick(event, notification) {
				event.preventDefault();

				if (!notification.link) {
					return;
				}

				if (!this.belongsToUser(notification)) {
					return;
				}

				this.$router.push({path: notification.link});
			},
			belongsToUser(notification) {
				if (notification.notifiable_type !== 'App\\User') {
					return;
				}

				return notification.notifiable_id === this.userId;
			}
		}
	}
</script>
